/**
 * ListingsSheet - the browse canvas's results: ONE list, and the chrome that
 * hosts it (#8722)
 *
 * Diamond Architecture:
 * - The list is `ResultsList`, built ONCE here and handed to exactly one host.
 * - The served host is `ResultsPane`, an ordinary in-flow element present at
 *   EVERY viewport, its geometry decided by CSS: the side panel (50 % width)
 *   at `md+`, and below `md` a static sheet over the map's lower edge, sized
 *   to the drawer's resting rail. That is what a reader with no JavaScript (or
 *   a failed script) sees, and what every document hydrates.
 * - Enhancement: once hydrated on a phone, the list moves into the vaul drawer
 *   (`MobileSheet`) at one of three rails (peek, half, full), positioned by
 *   the `results-sheet-state.ts` reducer that `useListingsMapState` owns — NOT
 *   by this file and not by vaul alone (obs. 5). A map gesture or the floating
 *   "Map" button asks for `peek`. A marker preview is the only thing that may
 *   hide the drawer, and it does so by BEING on screen: the drawer's openness
 *   is derived from the preview, so dismissing the preview is the return and
 *   there is no second state to restore.
 * - Receives proto-generated ListingCard[]
 *
 * Used by: ListingsClient on /listings page
 */
import { ResultsCount } from '@/shared/components/listings';
import type { ListingCardData } from '@/shared/lib/types/listing-card';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import type { ListingId } from '@letsgethome/types/branded';
import { useIsMobile } from '@letsgethome/ui/hooks/use-mobile';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import type { ListingsSurface } from './listings-surface';
import { MobileSheet } from './MobileSheet';
import {
  RESTING_POSITION,
  SNAP_FRACTION,
  type ResultsSheetEvent,
  type SheetPosition,
} from './results-sheet-state';
import { ResultsList } from './ResultsList';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/**
 * What the results pane holds for the key it is showing (#7816) — the query's
 * own state, typed, so each arm is something the pane must render rather than
 * a fallback it can forget. It replaced a `listings` / `totalCount` / `error`
 * triple whose missing data was spelled `?? initialListings` (a prop the key no
 * longer described) and then `?? []` (an error or a first load reading as "0
 * results").
 */
export type ListingsResults =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly listings: ListingCardData[]; readonly totalCount: number }
  /**
   * The served page stands past the search's last page (#6922) — a DECLARED
   * state of the canvas, not an empty list and not the 404 shell. The pane
   * states it; the pager (`footer`) carries the way back to page 1.
   */
  | { readonly kind: 'pastTheEnd'; readonly totalCount: number; readonly lastPage: number };

export interface ListingsSheetProps {
  /** The results, as the typed state above. */
  results: ListingsResults;
  /**
   * The map-sync affordance: the reader is now on a card (its id) or on none
   * (`null`). The hub highlights that listing's price pin.
   *
   * ★ This REPLACES `selectedId` + `onCardClick` (obs. 4). The list used to
   * receive the CARD-origin selection so it could draw a focus ring and scroll
   * the ringed card into view — both of which existed only because a click on a
   * card SELECTED it instead of opening it, and the reader needed to be told
   * what the click had done. A card opens on its first click now, so there is
   * nothing to ring and nothing to scroll to; the pin highlight is the whole of
   * the map↔list coupling. The pin's own styling still arrives separately, via
   * `ListingsMap.selectedId`.
   *
   * "On a card" is the POINTER **or** the keyboard — `ListingCard` reports both
   * through one channel (`CardSurface`, the card's root), so tabbing highlights
   * pins exactly as hovering it does, and a TOUCH tap reports nothing at all.
   */
  onCardHighlight?: (id: ListingId | null) => void;
  /** Callback when scrolled to bottom (for infinite scroll) */
  onLoadMore?: () => void;
  /** Whether more listings are being loaded */
  isLoadingMore?: boolean;
  /** Whether there are more listings to load */
  hasMore?: boolean;
  /**
   * The browse mode, which decides WHICH price a card shows.
   *
   * The mode itself, not an `isRentMode` boolean: the parent already holds a
   * `TransactionModeValue`, and passing a boolean meant destroying it here and
   * reconstructing it (`isRentMode ? 'rent' : 'sale'`) at every card — a
   * round-trip that silently returns the wrong value the day a third mode
   * exists.
   */
  mode: TransactionModeValue;
  /**
   * The mobile drawer's RAIL, reduced (`results-sheet-state.ts`).
   *
   * REQUIRED, and deliberately not optional: this replaces `onSnapToPeek` +
   * `mapInteractionSignal`, a callback and a monotonic counter that between
   * them could say only "something happened" and could not say where the
   * drawer was or how it got back. A sheet rendered without a state machine
   * is the defect (obs. 5), so it does not compile.
   *
   * The in-flow pane ignores this and the two fields below — it has no
   * position and no drawer — and only the drawer chrome is handed them.
   */
  sheetPosition: SheetPosition;
  /** Dispatch into the reducer that `useListingsMapState` owns. */
  onSheetEvent: (event: ResultsSheetEvent) => void;
  /**
   * The open marker preview, or `null` — the drawer's OPENNESS, handed over as
   * the fact it is derived from rather than as a boolean.
   *
   * ⛔ AND THAT IS THE POINT, not an accident of plumbing. The mobile pane does
   * not RENDER this listing (`ListingsClient` does, over the map); it reads
   * `resultsSheetIsOpen(markerPreview)`. Taking the fact instead of an `open`
   * boolean is what makes "the drawer is hidden and there is no preview on
   * screen" unspellable at this boundary: there is no second value that could
   * disagree with the first, and no caller that could compute one wrongly.
   * A stored `{ kind: 'hiddenForPreview' }` is exactly what that second value
   * was, and it is what stranded the drawer.
   */
  markerPreview: ListingCardData | null;
  /**
   * Rendered after the cards, inside the ONE list — the crawlable pager
   * (`ListingsPager`). A node rather than page/hasMore props so the sheet stays
   * ignorant of routes: the parent, which owns the loader meta, builds it. It
   * sits INSIDE the scroll container, after the last card, where a reader who
   * has scrolled to the end (and a crawler, which never scrolls) both find it.
   */
  footer?: ReactNode;
  /**
   * WHERE the canvas this list belongs to lives (#6915 / N1) — REQUIRED,
   * because the two answers are not a style difference.
   *
   * `canvas`: the page IS the viewport, so below `md` the results sit over
   * the map's lower edge — a static in-flow sheet in the served document, and
   * once a phone has hydrated `MobileSheet`, a vaul drawer whose content is
   * `fixed bottom-0` over the map — correct exactly while the map IS the
   * viewport.
   *
   * `inline`: the canvas is a block in a SCROLLING document. A viewport-fixed
   * drawer there is not a drawer, it is a band stuck over the page's own prose
   * at every scroll offset, so the list is rendered in flow beneath the map
   * instead — the same pane the desktop side panel has always been, at both
   * widths.
   */
  surface: ListingsSurface;
}

// ----------------------------------------------------------------------------
// The served host: the in-flow results pane
// ----------------------------------------------------------------------------

/** What the in-flow pane needs beyond the list it hosts. */
type ResultsPaneProps = Pick<ListingsSheetProps, 'results' | 'onLoadMore' | 'hasMore'> & {
  /** The ONE `ResultsList` — see `ListingsSheet`. */
  children: ReactNode;
};

function ResultsPane({ results, onLoadMore, hasMore, children }: ResultsPaneProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // (There is no scroll-into-view effect here any more: it followed the
  // card-origin selection, and a card that opens on its first click never
  // becomes "the selected card" the list has to go and find. obs. 4.)

  // Infinite scroll detection
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !onLoadMore || !hasMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMore]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header — the result count, from the SAME component the mobile
          drawer mounts. The sort `<select>` that sat beside it is retired:
          price ordering is not wanted, and a control the DESKTOP pane had
          while the mobile pane had none was half of obs. 2. This pane's own
          hand-written `<p>` is gone with it: two copies of one sentence is
          how the mobile one ended up left-aligned (obs. 3). */}
      <div className="px-4 pt-4 pb-3">
        {(results.kind === 'ready' || results.kind === 'pastTheEnd') && (
          <ResultsCount count={results.totalCount} />
        )}
      </div>

      {/* The scroll container; the list (cards, states, the crawlable pager)
          is the ONE `ResultsList` this pane was handed. */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
        {children}
      </div>
    </div>
  );
}

/**
 * The served pane's height below `md` on a `canvas` surface, DERIVED from the
 * drawer's resting rail (`SNAP_FRACTION[RESTING_POSITION]` of the viewport) —
 * never a second literal. Published as a custom property because Tailwind
 * scans source as text and cannot mint a class from a computed number; the
 * class that reads it is a literal below.
 *
 * `dvh` for the drawer's own reason (`MobileSheet`'s `SHEET_VIEWPORT_HEIGHT`):
 * the drawer rests at `snap × window.innerHeight`, so this is the same box the
 * drawer will occupy once it takes the list over — and the same strip the map
 * already centres itself above (`map-visible-inset.ts`).
 */
const RESTING_SHEET_HEIGHT_STYLE = {
  // `calc(fraction * 100dvh)`, not a pre-multiplied number: `0.55 * 100` is
  // `55.00000000000001` in IEEE-754, and CSS does the arithmetic exactly.
  '--resting-sheet-height': `calc(${String(SNAP_FRACTION[RESTING_POSITION])} * 100dvh)`,
} as CSSProperties;

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export function ListingsSheet(props: ListingsSheetProps) {
  const isMobile = useIsMobile();

  // ★ WHICH CHROME HOSTS THE LIST — and nothing else. The viewport hook no
  // longer decides whether the results EXIST (they always do: the list below
  // is built unconditionally and every arm of this component mounts it
  // exactly once). It decides only whether a HYDRATED phone has the drawer
  // enhancement. During hydration it answers the server's value (desktop), so
  // the hydration pass renders the served pane byte-for-byte and the drawer
  // takes over in the next commit.
  const drawerHostsList = props.surface === 'canvas' && isMobile;

  const list = (
    <ResultsList
      results={props.results}
      onCardHighlight={props.onCardHighlight}
      isLoadingMore={props.isLoadingMore}
      hasMore={props.hasMore}
      mode={props.mode}
      footer={props.footer}
      density={drawerHostsList ? 'drawer' : 'pane'}
    />
  );

  // An INLINE canvas has one list pane at every width — given the other half
  // of the block below `md`. The drawer is what makes a full-viewport canvas
  // usable; on a page that scrolls it is `fixed bottom-0` over the prose the
  // page exists to serve.
  if (props.surface === 'inline') {
    return (
      <div className="h-1/2 w-full min-h-0 border-t border-border md:h-full md:w-1/2 md:border-l md:border-t-0">
        <ResultsPane results={props.results} onLoadMore={props.onLoadMore} hasMore={props.hasMore}>
          {list}
        </ResultsPane>
      </div>
    );
  }

  if (drawerHostsList) {
    return (
      <MobileSheet
        results={props.results}
        onLoadMore={props.onLoadMore}
        hasMore={props.hasMore}
        sheetPosition={props.sheetPosition}
        onSheetEvent={props.onSheetEvent}
        markerPreview={props.markerPreview}
      >
        {list}
      </MobileSheet>
    );
  }

  return (
    // THE SERVED PANE, visible at EVERY viewport (#8722). It was
    // `hidden md:block`, which with the drawer client-only left a phone with no
    // JavaScript — or a failed script — an empty canvas over a document full
    // of cards. Its chrome is CSS now:
    //
    //   * `md+`: the side panel, half the row beside the map, as always;
    //   * below `md`: a static sheet over the map's lower edge, at the drawer's
    //     RESTING height (`RESTING_SHEET_HEIGHT_STYLE`). Not "below the map":
    //     the canvas is bounded to the viewport (`chrome: canvas`), the map
    //     pane is `absolute inset-0` over all of it, and the map has ALREADY
    //     centred its content above exactly this strip (`map-visible-inset.ts`)
    //     — so the no-JS phone shows the picture the hydrated one rests on, and
    //     a phone whose JavaScript does arrive sees the drawer take over the
    //     same box rather than the layout jump.
    //
    // ★ The bottom-edge SAFE-AREA inset is the drawer's own row
    // (`Z_LAYERS.resultsSheet.safeArea`), read from the same constant rather
    // than spelled again, so the two hosts of the one list clear a notched
    // phone's home indicator identically by construction. It sits where the
    // drawer puts it — on the element that owns the bottom edge, around the
    // scroller — and not on the scroller itself, whose `pb-4` it would replace
    // (`cn` merges the two `pb-*` utilities and the last one wins).
    <div
      data-slot="listings-results-pane"
      style={RESTING_SHEET_HEIGHT_STYLE}
      className={cn(
        'absolute inset-x-0 bottom-0 h-[var(--resting-sheet-height)] overflow-hidden rounded-t-2xl bg-background shadow-2xl',
        Z_LAYERS.resultsSheet.safeArea,
        'md:relative md:inset-auto md:h-full md:w-1/2 md:rounded-none md:shadow-none'
      )}
    >
      <ResultsPane results={props.results} onLoadMore={props.onLoadMore} hasMore={props.hasMore}>
        {list}
      </ResultsPane>
    </div>
  );
}
